/**
 * Database Seed Data
 *
 * Minimal clean seed data for first-time initialization.
 * Provides enough data to test all features without clutter.
 */

import { db } from './db';
import type { User, Tech } from '../types/ticket';
import type { Article, ArticleStep } from '../types/knowledgeBase';
import {
  UserRole,
  UserStatus,
  TechTier,
  Region,
  Location,
  TechWorkStatus,
  JobTag,
  ClientLOB,
  LOBProgram,
  LOBSite,
  AssignmentGroup,
} from '../types/ticket';
import { ArticleCategory, ArticleType, Department } from '../types/knowledgeBase';

/**
 * Seed the database with initial data
 * Called only when database is empty
 */
export async function seedDatabase(): Promise<void> {
  const now = new Date();

  // ==========================================
  // Users - IT staff + sample end users
  // ==========================================
  const users: User[] = [
    // IT Staff
    {
      id: 'user-admin-1',
      email: 'admin@percepta.com',
      name: 'System Admin',
      role: UserRole.ADMIN,
      department: Department.IT,
      status: UserStatus.ACTIVE,
      location: Location.US_DEARBORN,
      employeeId: 'EMP001',
      cdsid: 'sadmin1',
      clientLOB: ClientLOB.PERCEPTA_INTERNAL,
      lobProgram: LOBProgram.IT,
      lobSite: LOBSite.US_DEARBORN,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'user-daryn',
      email: 'Daryn.Paterson@Percepta.com',
      name: 'Daryn Paterson',
      role: UserRole.T2_TECH,
      department: Department.IT,
      status: UserStatus.ACTIVE,
      location: Location.UK_GLASGOW,
      employeeId: '4514501',
      cdsid: 'dpater11',
      clientLOB: ClientLOB.PERCEPTA_INTERNAL,
      lobProgram: LOBProgram.GA,
      lobSite: LOBSite.UK_GLASGOW,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'user-tech-2',
      email: 'marcus.johnson@percepta.com',
      name: 'Marcus Johnson',
      role: UserRole.T1_TECH,
      department: Department.IT,
      status: UserStatus.ACTIVE,
      location: Location.US_HOUSTON,
      employeeId: '4514502',
      cdsid: 'mjohns12',
      clientLOB: ClientLOB.PERCEPTA_INTERNAL,
      lobProgram: LOBProgram.IT,
      lobSite: LOBSite.US_HOUSTON,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'user-tech-3',
      email: 'sarah.chen@percepta.com',
      name: 'Sarah Chen',
      role: UserRole.T2_TECH,
      department: Department.IT,
      status: UserStatus.ACTIVE,
      location: Location.UK_DAVENTRY,
      employeeId: '4514503',
      cdsid: 'schen99',
      clientLOB: ClientLOB.PERCEPTA_INTERNAL,
      lobProgram: LOBProgram.IT,
      lobSite: LOBSite.UK_DAVENTRY,
      createdAt: now,
      updatedAt: now,
    },
    // End Users - Ford CRC
    {
      id: 'user-end-1',
      email: 'john.smith@percepta.com',
      name: 'John Smith',
      role: UserRole.T1_TECH,
      department: Department.OPERATIONS,
      status: UserStatus.ACTIVE,
      location: Location.US_DEARBORN,
      employeeId: '4520001',
      cdsid: 'jsmith45',
      clientLOB: ClientLOB.FORD,
      lobProgram: LOBProgram.CRC,
      lobSite: LOBSite.US_DEARBORN,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'user-end-2',
      email: 'emily.davis@percepta.com',
      name: 'Emily Davis',
      role: UserRole.T1_TECH,
      department: Department.HR,
      status: UserStatus.ACTIVE,
      location: Location.US_HOUSTON,
      employeeId: '4520002',
      cdsid: 'edavis22',
      clientLOB: ClientLOB.FORD,
      lobProgram: LOBProgram.TRUCK_CONCERN,
      lobSite: LOBSite.US_HOUSTON,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: 'user-end-3',
      email: 'michael.wilson@percepta.com',
      name: 'Michael Wilson',
      role: UserRole.T1_TECH,
      department: Department.FINANCE,
      status: UserStatus.ACTIVE,
      location: Location.UK_DAVENTRY,
      employeeId: '4520003',
      cdsid: 'mwilso33',
      clientLOB: ClientLOB.FORD,
      lobProgram: LOBProgram.INBOUND_CONCERN,
      lobSite: LOBSite.UK_DAVENTRY,
      createdAt: now,
      updatedAt: now,
    },
    // End Users - Lincoln
    {
      id: 'user-end-4',
      email: 'jennifer.brown@percepta.com',
      name: 'Jennifer Brown',
      role: UserRole.T1_TECH,
      department: Department.OPERATIONS,
      status: UserStatus.ACTIVE,
      location: Location.US_DEARBORN,
      employeeId: '4530001',
      cdsid: 'jbrown77',
      clientLOB: ClientLOB.LINCOLN,
      lobProgram: LOBProgram.LINCOLN_CRC,
      lobSite: LOBSite.US_DEARBORN,
      createdAt: now,
      updatedAt: now,
    },
    // End Users - Ford Pro
    {
      id: 'user-end-5',
      email: 'robert.taylor@percepta.com',
      name: 'Robert Taylor',
      role: UserRole.T1_TECH,
      department: Department.OPERATIONS,
      status: UserStatus.ACTIVE,
      location: Location.US_DEARBORN,
      employeeId: '4540001',
      cdsid: 'rtaylo88',
      clientLOB: ClientLOB.FORD_PRO,
      lobProgram: LOBProgram.FORD_PRO_COMMERCIAL,
      lobSite: LOBSite.US_DEARBORN,
      createdAt: now,
      updatedAt: now,
    },
    // Test User - Percepta Internal G&A
    {
      id: 'user-test-jsmith',
      email: 'John-Smith@Percepta.com',
      name: 'John Smith',
      role: UserRole.T1_TECH,
      department: Department.OPERATIONS,
      status: UserStatus.ACTIVE,
      location: Location.US_HOUSTON,
      employeeId: '00012345',
      cdsid: 'JSmith1',
      clientLOB: ClientLOB.PERCEPTA_INTERNAL,
      lobProgram: LOBProgram.GA,
      lobSite: LOBSite.US_HOUSTON,
      createdAt: now,
      updatedAt: now,
    },
  ];

  // ==========================================
  // Techs - linked to users, organized by assignment group
  // ==========================================
  const techs: Tech[] = [
    // Service Desk Team
    {
      id: 'tech-daryn',
      name: 'Daryn Paterson',
      tier: TechTier.T2,
      region: Region.UK,
      location: Location.UK_GLASGOW,
      skills: [
        JobTag.SOFTWARE,
        JobTag.O365,
        JobTag.TEAMS,
        JobTag.VPN,
        JobTag.PASSWORD_RESET,
        JobTag.MFA,
        JobTag.HARDWARE,
      ],
      currentLoad: 0,
      maxLoad: 5,
      isAvailable: true,
      userId: 'user-daryn',
      workStatus: TechWorkStatus.AVAILABLE,
      assignmentGroup: AssignmentGroup.SERVICE_DESK,
    },
    {
      id: 'tech-2',
      name: 'Marcus Johnson',
      tier: TechTier.T1,
      region: Region.US,
      location: Location.US_HOUSTON,
      skills: [
        JobTag.HARDWARE,
        JobTag.LAPTOP,
        JobTag.DESKTOP,
        JobTag.PASSWORD_RESET,
        JobTag.ACCOUNT_SETUP,
      ],
      currentLoad: 0,
      maxLoad: 5,
      isAvailable: true,
      userId: 'user-tech-2',
      workStatus: TechWorkStatus.AVAILABLE,
      assignmentGroup: AssignmentGroup.SERVICE_DESK,
    },
    {
      id: 'tech-4',
      name: 'Emily Rodriguez',
      tier: TechTier.T1,
      region: Region.US,
      location: Location.US_DEARBORN,
      skills: [
        JobTag.PASSWORD_RESET,
        JobTag.ACCOUNT_SETUP,
        JobTag.O365,
        JobTag.TEAMS,
      ],
      currentLoad: 2,
      maxLoad: 8,
      isAvailable: true,
      userId: 'user-tech-4',
      workStatus: TechWorkStatus.WORKING,
      assignmentGroup: AssignmentGroup.SERVICE_DESK,
    },
    // Networking Team
    {
      id: 'tech-3',
      name: 'Sarah Chen',
      tier: TechTier.T2,
      region: Region.UK,
      location: Location.UK_DAVENTRY,
      skills: [
        JobTag.SOFTWARE,
        JobTag.NETWORKING,
        JobTag.VPN,
        JobTag.FIREWALL,
        JobTag.ACCESS_REQUEST,
        JobTag.UK_SPECIFIC,
      ],
      currentLoad: 0,
      maxLoad: 5,
      isAvailable: true,
      userId: 'user-tech-3',
      workStatus: TechWorkStatus.AVAILABLE,
      assignmentGroup: AssignmentGroup.NETWORKING,
    },
    {
      id: 'tech-5',
      name: 'James Wilson',
      tier: TechTier.T2,
      region: Region.US,
      location: Location.US_DEARBORN,
      skills: [
        JobTag.NETWORKING,
        JobTag.VPN,
        JobTag.FIREWALL,
      ],
      currentLoad: 1,
      maxLoad: 5,
      isAvailable: true,
      userId: 'user-tech-5',
      workStatus: TechWorkStatus.AVAILABLE,
      assignmentGroup: AssignmentGroup.NETWORKING,
    },
    // Systems Team
    {
      id: 'tech-6',
      name: 'Michael Torres',
      tier: TechTier.T2,
      region: Region.US,
      location: Location.US_HOUSTON,
      skills: [
        JobTag.SOFTWARE,
        JobTag.DESKTOP,
        JobTag.LAPTOP,
      ],
      currentLoad: 0,
      maxLoad: 6,
      isAvailable: true,
      userId: 'user-tech-6',
      workStatus: TechWorkStatus.AVAILABLE,
      assignmentGroup: AssignmentGroup.SYSTEMS,
    },
    // Security Team
    {
      id: 'tech-7',
      name: 'Lisa Anderson',
      tier: TechTier.T2,
      region: Region.US,
      location: Location.US_DEARBORN,
      skills: [
        JobTag.MFA,
        JobTag.ACCESS_REQUEST,
        JobTag.FIREWALL,
      ],
      currentLoad: 0,
      maxLoad: 4,
      isAvailable: true,
      userId: 'user-tech-7',
      workStatus: TechWorkStatus.AVAILABLE,
      assignmentGroup: AssignmentGroup.SECURITY,
    },
  ];

  // ==========================================
  // Sample Articles - 3 knowledge base entries
  // ==========================================
  const createSteps = (steps: Omit<ArticleStep, 'id'>[]): ArticleStep[] =>
    steps.map((step, index) => ({
      ...step,
      id: crypto.randomUUID(),
      stepNumber: index + 1,
    }));

  const article1Steps = createSteps([
    {
      stepNumber: 1,
      title: 'Navigate to the Password Reset Portal',
      content:
        'Open your web browser and go to https://passwordreset.company.com. This portal is accessible from any device, even if you cannot log into your computer.',
    },
    {
      stepNumber: 2,
      title: 'Enter Your Username',
      content:
        'Enter your company email address or username in the provided field. Click "Next" to proceed to identity verification.',
    },
    {
      stepNumber: 3,
      title: 'Verify Your Identity',
      content:
        'Select your preferred verification method: phone call, text message, or authenticator app. Complete the verification to confirm your identity.',
    },
    {
      stepNumber: 4,
      title: 'Create Your New Password',
      content:
        'Enter your new password. It must be at least 12 characters long, include uppercase and lowercase letters, a number, and a special character. Cannot reuse your last 10 passwords.',
    },
    {
      stepNumber: 5,
      title: 'Confirm and Log In',
      content:
        'Click "Submit" to save your new password. Wait a few minutes for the change to sync across all systems, then log in with your new credentials.',
    },
  ]);

  const article2Steps = createSteps([
    {
      stepNumber: 1,
      title: 'Download Cisco Jabber',
      content:
        'Open the Software Center on your computer. Search for "Cisco Jabber" and click "Install". The download will begin automatically.',
    },
    {
      stepNumber: 2,
      title: 'Launch and Sign In',
      content:
        'Once installed, launch Cisco Jabber from the Start menu. Enter your company email address and click "Next". The app will auto-detect your server settings.',
    },
    {
      stepNumber: 3,
      title: 'Configure Audio Settings',
      content:
        'Go to Settings > Audio. Select your headset or speakers as the default audio device. Use the "Test" button to verify sound is working correctly.',
    },
    {
      stepNumber: 4,
      title: 'Set Up Voicemail',
      content:
        'Click on the phone icon, then go to Voicemail settings. Set up your voicemail PIN if prompted. Record a personalized greeting or use the default system greeting.',
    },
  ]);

  const article3Steps = createSteps([
    {
      stepNumber: 1,
      title: 'Open Cisco AnyConnect',
      content:
        'Find and open "Cisco AnyConnect Secure Mobility Client" from your Start menu or system tray. If not installed, download it from the Software Center.',
    },
    {
      stepNumber: 2,
      title: 'Enter the VPN Address',
      content:
        'In the connection field, type "vpn.company.com" and click "Connect". If this is your first time, you may need to accept the security certificate.',
    },
    {
      stepNumber: 3,
      title: 'Authenticate',
      content:
        'Enter your company username and password. If prompted for a second factor, approve the MFA request on your phone or enter the code from your authenticator app.',
    },
    {
      stepNumber: 4,
      title: 'Verify Connection',
      content:
        'Once connected, you will see a checkmark and "Connected to vpn.company.com" status. You can now access internal websites and network drives securely.',
    },
  ]);

  const articles: Article[] = [
    {
      id: 'article-1',
      title: 'How to Reset Your Password',
      description:
        'Step-by-step guide for resetting your network password through the self-service portal.',
      category: ArticleCategory.PASSWORD,
      tags: ['password', 'reset', 'self-service', 'account'],
      articleType: ArticleType.HOW_TO,
      articleContent: {
        type: 'how-to',
        content: { steps: article1Steps },
      },
      audiences: [Department.ALL],
      steps: article1Steps,
      authorId: 'user-daryn',
      authorName: 'Daryn',
      createdAt: now,
      updatedAt: now,
      viewCount: 0,
      isPublished: true,
    },
    {
      id: 'article-2',
      title: 'Setting Up Cisco Jabber',
      description:
        'Complete guide for installing and configuring Cisco Jabber for instant messaging and calls.',
      category: ArticleCategory.JABBER,
      tags: ['jabber', 'cisco', 'softphone', 'messaging', 'calls'],
      articleType: ArticleType.HOW_TO,
      articleContent: {
        type: 'how-to',
        content: { steps: article2Steps },
      },
      audiences: [Department.ALL],
      steps: article2Steps,
      authorId: 'user-daryn',
      authorName: 'Daryn',
      createdAt: now,
      updatedAt: now,
      viewCount: 0,
      isPublished: true,
    },
    {
      id: 'article-3',
      title: 'Connecting to VPN',
      description: 'How to connect to the company VPN for secure remote access to internal resources.',
      category: ArticleCategory.VPN,
      tags: ['vpn', 'remote', 'cisco', 'anyconnect', 'network'],
      articleType: ArticleType.HOW_TO,
      articleContent: {
        type: 'how-to',
        content: { steps: article3Steps },
      },
      audiences: [Department.ALL],
      steps: article3Steps,
      authorId: 'user-daryn',
      authorName: 'Daryn',
      createdAt: now,
      updatedAt: now,
      viewCount: 0,
      isPublished: true,
    },
    {
      id: 'article-4',
      title: 'VPN Connection Timeout After 5PM',
      description:
        'Known issue where VPN connections fail with timeout errors during evening hours due to server load balancing.',
      category: ArticleCategory.VPN,
      tags: ['vpn', 'timeout', 'connection', 'evening', 'known-issue'],
      articleType: ArticleType.KNOWN_ERROR,
      articleContent: {
        type: 'known-error',
        content: {
          affectedSystems: ['Cisco AnyConnect', 'Global Protect VPN', 'Remote Desktop Gateway'],
          symptoms:
            'Users experience connection timeouts when attempting to connect to VPN after 5PM local time. The error message typically shows "Connection attempt has timed out" or "Unable to establish VPN connection". This primarily affects users in US time zones during peak evening hours.',
          workaround:
            'To resolve this issue temporarily:\n\n1. Disconnect from any existing VPN attempts\n2. Clear your DNS cache by running "ipconfig /flushdns" in Command Prompt\n3. Connect to the alternate VPN server: vpn2.company.com instead of vpn.company.com\n4. If the issue persists, wait 5-10 minutes and retry the primary server\n\nThe IT team is working on a permanent fix by adding additional load balancing capacity to the primary VPN servers.',
          relatedArticleIds: ['article-3'],
        },
      },
      audiences: [Department.ALL],
      steps: [],
      authorId: 'user-daryn',
      authorName: 'Daryn',
      createdAt: now,
      updatedAt: now,
      viewCount: 0,
      isPublished: true,
    },
  ];

  // ==========================================
  // Insert all seed data in a transaction
  // ==========================================
  await db.transaction('rw', [db.users, db.techs, db.articles], async () => {
    await db.users.bulkAdd(users);
    await db.techs.bulkAdd(techs);
    await db.articles.bulkAdd(articles);
    // No tickets seeded - users create them
  });

  console.log('Database seeded successfully');
}
